import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DEFAULT_SHARED_STOREFRONT_CONTENT,
  normalizeSharedStorefrontContent,
  SHARED_STOREFRONT_CONTENT_KEY,
} from '@/lib/whiteLabelSharedContent';
import StorefrontSection from '@/components/StorefrontSection';
import { getSectionStylesForBreakpoint, useResponsiveBreakpoint } from '@/lib/responsiveStyles';
import { normalizeSafeUrl } from '@/lib/sanitizeHtml';

function readPreviewData(requestedPageIndex) {
  try {
    const search = new URLSearchParams(window.location.search);
    const pageParam = Number(search.get('page'));
    const activeTemplatePage = Number.isInteger(requestedPageIndex)
      ? requestedPageIndex
      : Number.isInteger(pageParam) && search.has('page')
        ? pageParam
        : null;
    if (search.get('source') === 'reseller') {
      const builderKey = search.get('builderKey');
      const resellerBuilder = builderKey?.startsWith('cloudsetu_page_builder_')
        ? JSON.parse(localStorage.getItem(builderKey) || 'null')
        : JSON.parse(localStorage.getItem('cloudsetu_page_builder_partner') || 'null');
      const resellerPageIndex = activeTemplatePage ?? resellerBuilder?.activeTemplatePage ?? 0;
      const resellerPage = resellerBuilder?.pages?.[resellerPageIndex];
      if (Array.isArray(resellerPage?.sections)) {
        return {
          sections: resellerPage.sections,
          contentOverridesBySectionType: resellerBuilder?.contentOverridesBySectionType || {},
          published: Boolean(resellerPage.published),
          activeTemplatePage: resellerPageIndex,
        };
      }
    }

    const builder = JSON.parse(localStorage.getItem('cloudsetu_admin_page_builder') || 'null');
    const adminPageIndex = activeTemplatePage !== null
      ? activeTemplatePage
      : builder?.activeTemplatePage || 0;
    const page = builder?.pages?.[adminPageIndex];
    if (Array.isArray(page?.sections) && page.sections.length) {
      return { sections: page.sections, published: Boolean(page.published), activeTemplatePage: adminPageIndex };
    }

    const resellerBuilder = JSON.parse(localStorage.getItem('cloudsetu_page_builder_partner') || 'null');
    const resellerPage = resellerBuilder?.pages?.[resellerBuilder.activeTemplatePage || 0];
    if (Array.isArray(resellerPage?.sections) && resellerPage.sections.length) {
      return { sections: resellerPage.sections, published: Boolean(resellerPage.published), activeTemplatePage: resellerBuilder.activeTemplatePage || 0 };
    }
  } catch (error) {
    console.error('Unable to read storefront preview data.', error);
  }

  return {
    sections: Object.entries(DEFAULT_SHARED_STOREFRONT_CONTENT).map(([type, content], order) => ({
      id: `preview-${type}`,
      type,
      order,
      visible: true,
      ...content,
    })),
    published: false,
  };
}

function readBrandingColors() {
  try {
    const saved = JSON.parse(localStorage.getItem('cloudsetu_reseller_branding_theme') || '{}');
    return {
      primary: saved.primary_color || '#0f172a',
      accent: saved.accent_color || '#3b82f6',
      background: saved.background_color || '#f8fafc',
      logoUrl: saved.logo_url || '',
      companyName: saved.company_name || '',
    };
  } catch (error) {
    console.error('Unable to read reseller branding colors for storefront preview.', error);
    return { primary: '#0f172a', accent: '#3b82f6', background: '#f8fafc', logoUrl: '', companyName: '' };
  }
}

export default function StorefrontPreview() {
  const [previewData, setPreviewData] = useState(readPreviewData);
  const [brandColors] = useState(readBrandingColors);
  const breakpoint = useResponsiveBreakpoint();
  const isResellerPreview = new URLSearchParams(window.location.search).get('source') === 'reseller';
  const isAdminPreview = new URLSearchParams(window.location.search).get('source') === 'admin';
  const pageIndex = previewData.activeTemplatePage || 0;
  const editPath = isResellerPreview
    ? `/reseller/white-label?tab=builder&edit=1&page=${pageIndex}`
    : isAdminPreview
      ? `/admin/white-label?tab=builder&page=${pageIndex}`
      : `/?tab=builder&page=${pageIndex}`;
  const allDesignsPath = isResellerPreview
    ? '/reseller/white-label?tab=builder'
    : isAdminPreview
      ? '/admin/white-label?tab=all-sites'
      : `/?tab=builder&page=${pageIndex}`;
  const sharedContent = useMemo(() => {
    try {
      return normalizeSharedStorefrontContent(JSON.parse(localStorage.getItem(SHARED_STOREFRONT_CONTENT_KEY) || 'null'));
    } catch (error) {
      console.error('Unable to read shared storefront content.', error);
      return normalizeSharedStorefrontContent(null);
    }
  }, []);

  const sections = previewData.sections
    .map(section => {
      const defaults = sharedContent[section.type] || {};
      const overrides = previewData.contentOverridesBySectionType?.[section.type]
        || section.contentOverrides
        || {};
      return {
        ...section,
        styles: getSectionStylesForBreakpoint(section, breakpoint),
        heading: overrides.heading ?? defaults.heading ?? '',
        kicker: overrides.kicker ?? defaults.kicker ?? '',
        subtext: overrides.subtext ?? defaults.subtext ?? '',
        primaryText: overrides.primaryText ?? defaults.primaryText ?? '',
        secondaryText: overrides.secondaryText ?? defaults.secondaryText ?? '',
        primaryUrl: overrides.primaryUrl ?? defaults.primaryUrl ?? section.primaryUrl ?? '',
        secondaryUrl: overrides.secondaryUrl ?? defaults.secondaryUrl ?? section.secondaryUrl ?? '',
        mediaUrl: overrides.mediaUrl ?? defaults.mediaUrl ?? section.mediaUrl ?? '',
        mediaAlt: overrides.mediaAlt ?? defaults.mediaAlt ?? section.mediaAlt ?? '',
        navigation: overrides.navigation ?? defaults.navigation ?? section.navigation ?? [],
        actions: overrides.actions ?? defaults.actions ?? section.actions ?? [],
        brandName: section.type === 'header' ? brandColors.companyName || 'Your brand' : undefined,
        brandLogo: section.type === 'header' && brandColors.logoUrl
          ? (brandColors.logoUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${brandColors.logoUrl}` : brandColors.logoUrl)
          : '',
        cards: overrides.cards ?? defaults.cards ?? [],
        inlineTextEdits: overrides.inlineTextEdits ?? defaults.inlineTextEdits ?? {},
      };
    })
    .filter(section => section.visible !== false);

  return (
    <div
      className="storefront-theme min-h-screen bg-slate-50"
      style={{
        '--storefront-primary': brandColors.primary,
        '--storefront-accent': brandColors.accent,
        '--storefront-background': brandColors.background,
      }}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Storefront Preview</h1>
          <p className="text-sm text-slate-500">Local preview of your saved builder content.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${previewData.published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
            {previewData.published ? 'Published' : 'Draft preview'}
          </span>
          <Link to={editPath} className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">Edit</Link>
          <Link to={allDesignsPath} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-blue-700">Back to all designs</Link>
        </div>
      </header>
      <main>
        {sections.map((section, index) => {
          return (
            <div key={section.id || `${section.type}-${index}`}>
              <StorefrontSection section={section} />
              {(section.blocks || []).length > 0 && (
                <div className="mx-auto max-w-7xl space-y-4 px-6 py-8">
                  {section.blocks.map(block => {
                    const blockStyle = section.styles?.[`block:${block.id}`] || {};
                    if (block.type === 'heading') return <h3 key={block.id} className="text-xl font-bold" style={blockStyle}>{block.title}</h3>;
                    if (block.type === 'button') return <a key={block.id} href={normalizeSafeUrl(block.href || '#contact')} className="inline-flex rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white" style={blockStyle}>{block.title}</a>;
                    if (block.type === 'card' || block.type === 'container') return <div key={block.id} className="rounded-xl border border-slate-200 bg-white p-5 text-slate-900 shadow-sm" style={blockStyle}><h3 className="font-semibold">{block.title}</h3><p className="mt-2 whitespace-pre-line text-sm text-slate-600">{block.text}</p>{(block.children || []).map(child => child.type === 'heading' ? <h4 key={child.id} className="mt-3 font-semibold">{child.text}</h4> : <p key={child.id} className="mt-2 text-sm text-slate-600">{child.text}</p>)}</div>;
                    if (block.type === 'shape') return <div key={block.id} aria-label="Decorative shape" className="h-12 w-20 rounded-md bg-blue-500" style={blockStyle} />;
                    return <p key={block.id} className="whitespace-pre-line" style={blockStyle}>{block.title}</p>;
                  })}
                </div>
              )}
            </div>
          );
        })}
        {!sections.length && <p className="p-12 text-center text-slate-500">No visible sections are available to preview.</p>}
      </main>
    </div>
  );
}
